import { SDUIComponentClientCodeHandler } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/SDUIComponentRenderer/__internal__/SDUIComponentClientCodeHandler";
import { getContentWidthCss } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/DEPRECATED_componentContentWidth";
import { getFallbackHorizonalSpacingPixels } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/horizontalPadding";
import {
  getConsolidatedSpacingValues,
  getResponsiveSpacingPixels,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/spacing";
import {
  ComponentLoggingEventParams,
  SDUIComponentInstanceRendererProps,
  SDUIComponentRenderBehaviour,
  SectionLoggingEventParams,
} from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIViewportHandler } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Viewport/SDUIViewportHandler";
import { ComponentRenderMap } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/types";
import {
  assignInlineVars,
  DynamicVariable,
  mapResponsivePropToCSSVars,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { getSettingSDUITestModeEnabled } from "@faire/deprecated-retailer-visitor-shared/settings/getSettingSDUITestModeEnabled";
import { getColor } from "@faire/sdui/lib/getColor";
import { ColorTokenKey } from "@faire/web-api--source/indigofair/data/ColorTokenKey";
import { IComponent } from "@faire/web-api--source/indigofair/data/IComponent";
import { cn } from "@faire/web/tools/cn";
import { WhenVisibilityIsImminent } from "@faire/web/ui/VisibilitySensor/WhenVisibilityIsImminent";
import React, { CSSProperties, useMemo } from "react";

const varMobilePadding: DynamicVariable<"--f-sdui-c-padding"> = {
  variable: "--f-sdui-c-padding",
  className: "p-(--f-sdui-c-padding)",
};

const varTabletPadding: DynamicVariable<"--f-sdui-c-padding-md"> = {
  variable: "--f-sdui-c-padding-md",
  className: "md:p-(--f-sdui-c-padding-md)",
};

const varDesktopPadding: DynamicVariable<"--f-sdui-c-padding-lg"> = {
  variable: "--f-sdui-c-padding-lg",
  className: "lg:p-(--f-sdui-c-padding-lg)",
};

const varXLargePadding: DynamicVariable<"--f-sdui-c-padding-xl"> = {
  variable: "--f-sdui-c-padding-xl",
  className: "xl:p-(--f-sdui-c-padding-xl)",
};

interface SDUIComponentRendererProps {
  component: IComponent;
  componentRenderers: ComponentRenderMap;
  sectionLoggingParams: SectionLoggingEventParams;
  isPrimaryContent: boolean;
  renderBehaviourOverrides?: SDUIComponentRenderBehaviour;
  disableViewEvent?: boolean;
  loadTimingRef?: React.RefCallback<HTMLElement>;
  onClickClientDismissHandler?: () => void;
}

const VISIBILITY_THRESHOLD = {
  desktopAndAbove: -900,
  tabletAndAbove: -800,
  mobileAndAbove: -700,
};

export const SDUIComponentRenderer: React.FC<SDUIComponentRendererProps> = ({
  component,
  componentRenderers,
  sectionLoggingParams,
  isPrimaryContent,
  renderBehaviourOverrides,
  disableViewEvent,
  loadTimingRef,
  onClickClientDismissHandler,
}) => {
  const loggingEventParams: ComponentLoggingEventParams = useMemo(
    () =>
      ComponentLoggingEventParams.build({
        ...sectionLoggingParams,
        ...component.logging_data,
        component_id: component.id,
      }),
    [sectionLoggingParams, component]
  );
  const ComponentLoadingStateRenderer = getComponentRenderer(
    component,
    componentRenderers,
    "pending"
  );
  const shouldRenderLoadingState =
    ComponentLoadingStateRenderer !== undefined && !isPrimaryContent;

  if (shouldRenderLoadingState) {
    return (
      <WhenVisibilityIsImminent
        customizePlaceholder={
          <div
            className="mx-auto my-0 h-[inherit] w-full"
            style={getContentWidthCss(
              component.style?.content_width,
              loggingEventParams
            )}
          >
            <ComponentLoadingStateRenderer
              component={component}
              loggingData={loggingEventParams}
            />
          </div>
        }
        threshold={VISIBILITY_THRESHOLD}
        useTransition
        shouldRunNormallyInTest
      >
        <SDUIComponentRendererBase
          component={component}
          componentRenderers={componentRenderers}
          disableViewEvent={disableViewEvent}
          loggingData={loggingEventParams}
          isPrimaryContent={isPrimaryContent}
          renderBehaviourOverrides={renderBehaviourOverrides}
          loadTimingRef={loadTimingRef}
        />
      </WhenVisibilityIsImminent>
    );
  } else {
    return (
      <SDUIComponentRendererBase
        component={component}
        componentRenderers={componentRenderers}
        isPrimaryContent={isPrimaryContent}
        loadTimingRef={loadTimingRef}
        loggingData={loggingEventParams}
        disableViewEvent={disableViewEvent}
        renderBehaviourOverrides={renderBehaviourOverrides}
        onClickClientDismissHandler={onClickClientDismissHandler}
      />
    );
  }
};

const SDUIComponentRendererBase: React.FC<
  Omit<SDUIComponentRendererProps, "sectionLoggingParams"> & {
    loggingData: ComponentLoggingEventParams;
  }
> = ({
  component,
  componentRenderers,
  loggingData,
  isPrimaryContent,
  disableViewEvent,
  renderBehaviourOverrides,
  loadTimingRef,
  onClickClientDismissHandler,
}) => {
  const ComponentRenderer = getComponentRenderer(component, componentRenderers);

  if (!ComponentRenderer) {
    return null;
  }

  // Needed to prevent logging of onView events for spacer components
  const isSpacerElement = component.spacer_component !== undefined;

  const renderBehaviour: SDUIComponentRenderBehaviour = {
    imagesLoadingStrategy: isPrimaryContent ? "eager" : undefined,
    deferInitializationUntilView: !isPrimaryContent,
    ...renderBehaviourOverrides,
  };

  const componentLevelSpacing = getComponentLevelSpacing(
    component,
    loggingData
  );

  return (
    <SDUIViewportHandler viewports={component.viewports}>
      <SDUIComponentBackgroundColor color={component.style?.background_color}>
        <SDUIComponentClientCodeHandler
          className={componentLevelSpacing.className}
          componentStyle={componentLevelSpacing.styles}
          loggingData={loggingData}
          onViewActions={component.on_view}
          ref={loadTimingRef}
          isRequiredForInteractivity={isPrimaryContent}
          logSDUIViewEvent={!isSpacerElement && !disableViewEvent}
          logLegacyViewEvent={
            component.banner_component !== undefined && !disableViewEvent
          }
        >
          <ComponentRenderer
            component={component}
            loggingData={loggingData}
            renderBehaviour={renderBehaviour}
            onClickClientDismissHandler={onClickClientDismissHandler}
          />
        </SDUIComponentClientCodeHandler>
      </SDUIComponentBackgroundColor>
    </SDUIViewportHandler>
  );
};

const SDUIComponentBackgroundColor = ({
  color,
  children,
}: {
  color: keyof typeof ColorTokenKey | undefined;
  children: React.ReactNode;
}) => {
  return (
    <div
      className="h-[inherit] w-full"
      style={{
        backgroundColor: getColor(color, ColorTokenKey.COLOR_TOKEN_KEY_UNKNOWN),
      }}
    >
      {children}
    </div>
  );
};

const getComponentLevelSpacing = (
  component: IComponent,
  loggingData: ComponentLoggingEventParams
): { className: string; styles: CSSProperties } => {
  // To is needed for backwards compatible styling with LayoutElements
  const isLayoutElement = component.layout_element !== undefined;

  if (isLayoutElement) {
    return {
      className: cn(
        "h-[inherit] w-full",
        getSettingSDUITestModeEnabled()
          ? "border-4 border-solid border-red-500"
          : undefined
      ),
      styles: {},
    };
  } else {
    const {
      top_spacing,
      responsive_top_spacing,
      bottom_spacing,
      responsive_bottom_spacing,
      horizontal_spacing,
      responsive_horizontal_spacing,
    } = component.style ?? IComponent.IComponentStyle.build();

    const responsivePaddingTop = getResponsiveSpacingPixels(
      top_spacing,
      responsive_top_spacing
    );

    const responsivePaddingBottom = getResponsiveSpacingPixels(
      bottom_spacing,
      responsive_bottom_spacing
    );

    const responsiveHorizontalPadding =
      horizontal_spacing !== undefined ||
      responsive_horizontal_spacing !== undefined
        ? getResponsiveSpacingPixels(
            horizontal_spacing,
            responsive_horizontal_spacing
          )
        : getFallbackHorizonalSpacingPixels(component, loggingData);

    const spacing = getConsolidatedSpacingValues(
      responsivePaddingTop,
      responsivePaddingBottom,
      responsiveHorizontalPadding,
      responsiveHorizontalPadding
    );

    const { classes: paddingClassnames, vars: paddingStyles } =
      mapResponsivePropToCSSVars(spacing, {
        mobileAndAbove: varMobilePadding,
        tabletAndAbove: varTabletPadding,
        desktopAndAbove: varDesktopPadding,
        xLargeAndAbove: varXLargePadding,
      });

    return {
      className: cn("mx-auto my-0 h-[inherit] w-full", paddingClassnames),
      styles: {
        ...getContentWidthCss(component.style?.content_width, loggingData),
        ...assignInlineVars(paddingStyles),
      },
    };
  }
};

const getComponentRenderer = (
  component: IComponent,
  componentRenderers: ComponentRenderMap,
  state: "active" | "pending" = "active"
): React.FC<SDUIComponentInstanceRendererProps> | undefined => {
  if (component.layout_element) {
    return componentRenderers["layout_element"]?.[state];
  } else if (component.carousel_component) {
    return componentRenderers["carousel_component"]?.[state];
  } else if (component.banner_component) {
    return componentRenderers["banner_component"]?.[state];
  } else if (component.spacer_component) {
    return componentRenderers["spacer_component"]?.[state];
  } else if (component.header_component) {
    return componentRenderers["header_component"]?.[state];
  } else if (component.grid_component) {
    return componentRenderers["grid_component"]?.[state];
  } else if (component.collapsible_list_web_banner_component) {
    return componentRenderers["collapsible_list_web_banner_component"]?.[state];
  } else if (component.alert_banner_component) {
    return componentRenderers["alert_banner_component"]?.[state];
  } else if (component.site_banner_component) {
    return componentRenderers["site_banner_component"]?.[state];
  } else if (component.tile_wrapper_component) {
    return componentRenderers["tile_wrapper_component"]?.[state];
  } else if (component.filter_bar_component) {
    return componentRenderers["filter_bar_component"]?.[state];
  } else if (component.dual_content_banner_component) {
    return componentRenderers["dual_content_banner_component"]?.[state];
  } else if (component.market_banner_component) {
    return componentRenderers["market_banner_component"]?.[state];
  } else if (component.main_value_props_component) {
    return componentRenderers["main_value_props_component"]?.[state];
  } else if (component.flow_row_component) {
    return componentRenderers["flow_row_component"]?.[state];
  } else if (component.search_suggestions_component) {
    return componentRenderers["search_suggestions_component"]?.[state];
  } else if (component.simple_action_banner_component) {
    return componentRenderers["simple_action_banner_component"]?.[state];
  } else if (component.client_driven_component) {
    return componentRenderers["client_driven_component"]?.[state];
  }
  return undefined;
};
